// @flow
import * as React from 'react';
import {
    Box, Text, TextInput, Stack,
} from 'grommet';
import { NumberInput as SpeedNumberInput, FieldWrapper } from 'speed-js-react-web';
import styled from 'styled-components';
import get from 'lodash/get';
import StyledCloseButton
    from './StyledCloseButton';


const StyledBox = styled(Box)`
    position: absolute;
    right: 10px
`;

const StyledText = styled(Text)`
    line-height: 9px;
`;

type Props = {
    field: {
        onFocus: (e: SyntheticFocusEvent<HTMLInputElement>) => void,
        onBlur: (e: SyntheticFocusEvent<any>) => void,
        onChange: (e: SyntheticInputEvent<HTMLInputElement>) => void,
        value: any,
        name: string
    },
    form: {
        errors: {
            [string]: Object
        },
        touched: {
            [string]: boolean
        },
        isSubmitting: boolean,
        setFieldValue: (string, any) => void,
    },
    required: boolean,
    label: string,
    help_text: string,
    defaultValue: number,
    min_value: number,
    max_value: number,
    disabled?: boolean,
    step?: number,
    unit?: string,
    placeholder?: string,
}

const CentsInputFormik = (props: Props): React.Node => {
    const {
        disabled,
        field: {
            name, onChange, onBlur, value,
        },
        form: {
            touched, errors, isSubmitting, setFieldValue,
        },
        label,
        help_text: help,
        required,
        min_value: minValue,
        max_value: maxValue,
        defaultValue,
        step,
        unit,
        placeholder,
    } = props;

    React.useEffect(() => {
        if (
            (typeof value === 'undefined' || value === null || value === '')
            && typeof defaultValue !== 'undefined' && defaultValue !== null
        ) {
            onChange({ target: { value: defaultValue, name } });
            setFieldValue(name, defaultValue);
        } else if (typeof value !== 'undefined' && value !== null && minValue !== null && value < minValue) {
            setFieldValue(name, minValue);
        } else if (typeof value !== 'undefined' && value !== null && maxValue !== null && value > maxValue) {
            setFieldValue(name, maxValue);
        }
    }, [value, maxValue, minValue, name, setFieldValue, defaultValue, onChange]);

    const onClear = () => {
        const clearedValue = typeof defaultValue !== 'undefined' ? defaultValue : null;
        onChange({ target: { value: clearedValue, name } });
        setFieldValue(name, clearedValue);
    };

    const handleChange = React.useCallback((e) => {
        onChange(e);
        if (e.target.value !== '') {
            let cents = parseFloat(e.target.value);
            if (Number.isNaN(cents)) {
                cents = 0;
            }
            cents *= 100;
            setFieldValue(name, Number.parseInt(cents.toFixed(0), 10));
        } else {
            setFieldValue(name, null);
        }
    }, [onChange, setFieldValue, name]);

    const formattedValue = React.useMemo(() => {
        let result = '';
        if (value !== null && value !== '') {
            result = value / 100.00;
        }

        return result;
    }, [value]);

    return (
        <FieldWrapper
            name={name}
            required={required}
            helpText={help}
            label={label}
        >
            <Stack anchor="right">
                <TextInput
                    type="number"
                    name={name}
                    onChange={handleChange}
                    placeholder={placeholder}
                    onBlur={onBlur}
                    value={formattedValue}
                    id={`id-${name}`}
                    disabled={disabled || isSubmitting}
                    min={minValue}
                    max={maxValue}
                    step={step}
                    errors={get(errors, name)}
                    touched={get(touched, name)}
                    style={{ paddingRight: unit ? 46 : 32 }}
                />
                {value && (
                    <StyledBox style={{ top: -16, right: unit ? 50 : 32 }}>
                        <StyledCloseButton onClick={onClear} plain />
                    </StyledBox>
                )}
            </Stack>
        </FieldWrapper>
    );
};


CentsInputFormik.getValidationSchema = SpeedNumberInput.getValidationSchema;
CentsInputFormik.defaultProps = {
    disabled: false,
    step: '0.01',
    unit: undefined,
    placeholder: '',
};

export default CentsInputFormik;
